import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EmbeddedViewRef,
  EnvironmentInjector,
  Injectable,
  Injector,
  Type
} from "@angular/core";
import {Subject} from "rxjs";
import {take} from "rxjs/operators";
import {ModalWrapperComponent} from "../../shared-module-ui/components/modal-wrapper/modal-wrapper.component";

export interface IModalOptions<T = unknown, P = unknown> {
  component: Type<T>;
  backdropDismiss?: boolean;
  componentProps?: P;
  cssClass?: string | string[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface IModalDismissResult<T = any> {
  // data: {
  //   ['button-pressed']: 'button-accept' | 'button-primary' | 'button-secondary' | 'button-cancel' | 'button-delete'
  // };
  data: T;
  role: string | undefined;
}

export interface IModalRef<T = unknown> {
  dismiss(data?: T, role?: string): Promise<boolean>;

  onDidDismiss(): Promise<IModalDismissResult>;

  present(): Promise<void>;
}

@Injectable({
  providedIn: "root"
})
export class ModalControllerService {
  private activeModals: ModalInstance[] = [];

  constructor(
    private injector: Injector,
    private appRef: ApplicationRef
  ) {
  }

  async create(opts: IModalOptions): Promise<IModalRef> {
    const modal = new ModalInstance(
      this.injector,
      this.appRef,
      opts,
      () => {
        this.removeInstance(modal);
      }
    );
    this.activeModals.push(modal);
    return modal.getApi();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async dismiss(data?: any, role?: string): Promise<boolean> {
    const topInstance = this.getTopInstance();
    if (topInstance) {
      return topInstance.dismiss(data, role);
    }
    // If no modal is open, immediately resolve false.
    return Promise.resolve(false);
  }

  getTop(): IModalRef | undefined {
    const topInstance = this.getTopInstance();
    return topInstance ? topInstance.getApi() : undefined;
  }

  private getTopInstance(): ModalInstance | undefined {
    return this.activeModals[this.activeModals.length - 1];
  }

  private removeInstance(instance: ModalInstance): void {
    const idx = this.activeModals.indexOf(instance);
    if (idx > -1) {
      this.activeModals.splice(idx, 1);
    }
  }
}


class ModalInstance {
  private wrapperRef: ComponentRef<ModalWrapperComponent>;
  private didDismiss = new Subject<IModalDismissResult>();
  private lastRole: string | undefined;

  constructor(
    private injector: Injector,
    private appRef: ApplicationRef,
    private opts: IModalOptions,
    private onRemove: () => void
  ) {
    this.wrapperRef = createComponent(ModalWrapperComponent, {
      environmentInjector: this.injector as EnvironmentInjector
    });

    this.wrapperRef.instance.component = this.opts.component;
    this.wrapperRef.instance.componentProps = this.opts.componentProps || {};
    this.wrapperRef.instance.cssClass = this.opts.cssClass;
    this.wrapperRef.instance.backdropDismiss =
      this.opts.backdropDismiss !== false;

    this.wrapperRef.instance.willDismiss$.pipe(take(1)).subscribe((payload) => {
      this.didDismiss.next({data: payload, role: this.lastRole});
      this.didDismiss.complete();
      this.onRemove();
      this.cleanup();
    });
  }

  async present<T = unknown>(): Promise<void> {
    this.appRef.attachView(this.wrapperRef.hostView);
    const hostView = this.wrapperRef.hostView as EmbeddedViewRef<T>;
    const domElem = hostView.rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);
    this.wrapperRef.instance.present();
  }

  async dismiss<T = unknown>(data?: T, role?: string): Promise<boolean> {
    this.lastRole = role;

    return new Promise<boolean>((resolve) => {
      this.wrapperRef.instance.willDismiss$.pipe(take(1)).subscribe(() => {
        resolve(true);
      });

      this.wrapperRef.instance.dismiss(data);
    });
  }

  async onDidDismiss(): Promise<IModalDismissResult> {
    return this.didDismiss.toPromise();
  }

  private cleanup(): void {
    this.appRef.detachView(this.wrapperRef.hostView);
    this.wrapperRef.destroy();
  }

  getApi<T = unknown>(): IModalRef {
    return {
      present: () => this.present(),
      dismiss: <U = T>(data?: T, role?: string) => this.dismiss(data, role),
      onDidDismiss: () => this.onDidDismiss()
    };
  }
}

//import {
//   ApplicationRef,
//   ComponentRef,
//   createComponent,
//   EnvironmentInjector,
//   Injectable,
//   Injector,
//   Type,
// } from '@angular/core';
// import {Subject} from 'rxjs';
// import {take} from 'rxjs/operators';
// import {ModalWrapperComponent} from '../../shared-module-ui/components/modal-wrapper/modal-wrapper.component';
//
// export interface IModalOptions<T = unknown, P = unknown> {
//   component: Type<T>;
//   backdropDismiss?: boolean;
//   componentProps?: P;
//   cssClass?: string | string[];
// }
//
// export interface IModalDismissResult<T = unknown> {
//   data: T | null;
//   role: string | undefined;
// }
//
// export interface IModalRef<T = unknown> {
//   dismiss(data?: T, role?: string): Promise<boolean>;
//
//   onDidDismiss<U = T>(): Promise<IModalDismissResult<U>>;
//
//   present(): Promise<void>;
// }
//
// @Injectable({
//   providedIn: 'root',
// })
// export class ModalControllerService {
//   private activeModals: ModalInstance[] = [];
//
//   constructor(
//     private injector: Injector,
//     private appRef: ApplicationRef,
//   ) {
//   }
//
//   async create(opts: IModalOptions): Promise<IModalRef> {
//     const modal = new ModalInstance(
//       this.injector,
//       this.appRef,
//       opts,
//       () => {
//         this.removeInstance(modal);
//       },
//     );
//     this.activeModals.push(modal);
//     return modal.getApi();
//   }
//
//   async dismiss<T = unknown>(data?: T, role?: string): Promise<boolean> {
//     const topInstance = this.getTopInstance();
//     if (topInstance) {
//       return topInstance.dismiss(data, role);
//     }
//     // If no modal is open, immediately resolve false.
//     return Promise.resolve(false);
//   }
//
//   getTop(): IModalRef | undefined {
//     const topInstance = this.getTopInstance();
//     return topInstance ? topInstance.getApi() : undefined;
//   }
//
//   private getTopInstance(): ModalInstance | undefined {
//     return this.activeModals[this.activeModals.length - 1];
//   }
//
//   private removeInstance(instance: ModalInstance): void {
//     const idx = this.activeModals.indexOf(instance);
//     if (idx > -1) {
//       this.activeModals.splice(idx, 1);
//     }
//   }
// }
//
// class ModalInstance<T = unknown, P = unknown> {
//   private wrapperRef: ComponentRef<ModalWrapperComponent>;
//   private didDismiss = new Subject<IModalDismissResult<T>>();
//   private lastRole: string | undefined;
//
//   constructor(
//     private injector: Injector,
//     private appRef: ApplicationRef,
//     private opts: IModalOptions<T, P>,
//     private onRemove: () => void,
//   ) {
//     this.wrapperRef = createComponent(ModalWrapperComponent, {
//       environmentInjector: this.injector as EnvironmentInjector,
//     });
//
//     this.wrapperRef.instance.component = this.opts.component;
//     this.wrapperRef.instance.componentProps = this.opts.componentProps || {};
//     this.wrapperRef.instance.cssClass = this.opts.cssClass;
//     this.wrapperRef.instance.backdropDismiss =
//       this.opts.backdropDismiss !== false;
//
//     this.wrapperRef.instance.willDismiss$.pipe(take(1)).subscribe((payload: T) => {
//       this.didDismiss.next({data: payload, role: this.lastRole});
//       this.didDismiss.complete();
//       this.onRemove();
//       this.cleanup();
//     });
//   }
//
//   async present<T = unknown>(): Promise<void> {
//     this.appRef.attachView(this.wrapperRef.hostView);
//     const domElem = (this.wrapperRef.hostView as any).rootNodes[0] as HTMLElement;
//     document.body.appendChild(domElem);
//     this.wrapperRef.instance.present();
//   }
//
//   async dismiss(data?: T, role?: string): Promise<boolean> {
//     this.lastRole = role;
//
//     return new Promise<boolean>((resolve) => {
//       this.wrapperRef.instance.willDismiss$.pipe(take(1)).subscribe(() => {
//         resolve(true);
//       });
//
//       this.wrapperRef.instance.dismiss(data);
//     });
//   }
//
//   async onDidDismiss(): Promise<IModalDismissResult<T>> {
//     return this.didDismiss.toPromise();
//   }
//
//   private cleanup(): void {
//     this.appRef.detachView(this.wrapperRef.hostView);
//     this.wrapperRef.destroy();
//   }
//
//   getApi(): IModalRef<T> {
//     return {
//       present: () => this.present(),
//       dismiss: (data?: T, role?: string) => this.dismiss(data, role),
//       onDidDismiss: <U = T>() => this.onDidDismiss() as unknown as Promise<IModalDismissResult<U>>,
//     };
//   }
// }